import { validatedEventId, type BeaconEvent } from "@/lib/beacon-types";

export const DURABLE_ANALYTICS_OUTBOX_KEY = "ishvaram_durable_analytics_outbox_v1";

const MAX_DURABLE_EVENTS = 32;
const DURABLE_EVENT_NAMES: ReadonlySet<string> = new Set([
  "agent_revenue_offer_shown",
  "offer_card_seen",
  "report_checkout_started",
]);

export type DurableBeaconEvent = BeaconEvent & {
  event_id: string;
  client_id: string;
  params: Record<string, unknown>;
};

function browserStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isDurableEvent(value: unknown): value is DurableBeaconEvent {
  if (!isRecord(value)) return false;
  return (
    typeof value.name === "string" &&
    DURABLE_EVENT_NAMES.has(value.name) &&
    validatedEventId(value.event_id) !== null &&
    typeof value.client_id === "string" &&
    value.client_id.length > 0 &&
    isRecord(value.params)
  );
}

function writeOutbox(storage: Storage, events: DurableBeaconEvent[]): void {
  try {
    if (events.length === 0) storage.removeItem(DURABLE_ANALYTICS_OUTBOX_KEY);
    else storage.setItem(DURABLE_ANALYTICS_OUTBOX_KEY, JSON.stringify(events));
  } catch {
    // Storage-denied browsers retain the existing in-memory delivery path.
  }
}

export function pendingDurableEvents(): DurableBeaconEvent[] {
  const storage = browserStorage();
  if (!storage) return [];
  try {
    const decoded: unknown = JSON.parse(storage.getItem(DURABLE_ANALYTICS_OUTBOX_KEY) ?? "[]");
    return Array.isArray(decoded) ? decoded.filter(isDurableEvent) : [];
  } catch {
    return [];
  }
}

export function persistDurableEvent(event: DurableBeaconEvent): void {
  if (!DURABLE_EVENT_NAMES.has(event.name)) return;
  const storage = browserStorage();
  if (!storage) return;
  const retained = pendingDurableEvents().filter((row) => row.event_id !== event.event_id);
  writeOutbox(storage, [...retained, event].slice(-MAX_DURABLE_EVENTS));
}

export function acknowledgeDurableEvents(eventIds: ReadonlySet<string>): void {
  if (eventIds.size === 0) return;
  const storage = browserStorage();
  if (!storage) return;
  writeOutbox(
    storage,
    pendingDurableEvents().filter((event) => !eventIds.has(event.event_id))
  );
}
